import {
  CircularProgress,
  LinearProgress,
  LinearProgressProps,
} from "@mui/material";
import { isNil } from "lodash";
import { memo, ReactElement, ReactNode } from "react";

import { RydeProgressStyled } from "./ryde-progress.styled";

const FREQUENCY_TO_UPDATE = 5;

// Slowdown for too frequent update.
const arePropsEqual = (
  { value, title, color, displayValue }: IRydeProgressProps,
  {
    value: newValue,
    title: newTitle,
    color: newColor,
    displayValue: newDisplayValue,
  }: IRydeProgressProps,
): boolean => {
  if (title !== newTitle) {
    return false;
  }

  if (color !== newColor) {
    return false;
  }

  if (displayValue !== newDisplayValue) {
    return false;
  }

  if (newValue !== undefined && newValue >= 100) {
    return false;
  }

  if (newValue !== undefined && value !== undefined) {
    return newValue - value <= FREQUENCY_TO_UPDATE;
  }

  return false;
};

interface IRydeProgressProps {
  value: LinearProgressProps["value"];
  title?: string;
  displayValue?: string | number;
  total?: number;
  completed?: number;
  color?: LinearProgressProps["color"];
  EndAdornment?: ReactNode;
  className?: string;
  isLoading?: boolean;
}

export const RydeProgress = memo(
  ({
    value,
    displayValue,
    total,
    completed,
    title,
    color = "primary",
    EndAdornment,
    className,
    isLoading,
  }: IRydeProgressProps): ReactElement => {
    const hasCountProgress = !!total && !!completed;

    return (
      <RydeProgressStyled
        className={className}
        $isEndAdornment={Boolean(EndAdornment)}
      >
        {Boolean(title) && (
          <div className="RydeProgress-Head__container">
            <div className="RydeProgress-Title">
              {title}
              {hasCountProgress && (
                <span className="RydeProgress-Count">
                  {completed} / {total}
                </span>
              )}
              {!isNil(displayValue) && (
                <span className="RydeProgress-DisplayValue">
                  {displayValue}
                </span>
              )}
            </div>
            {isLoading && (
              <CircularProgress
                size={16}
                thickness={5}
                color={color ?? "primary"}
              />
            )}
          </div>
        )}
        <LinearProgress
          className="RydeProgress-Line"
          variant={!isNil(value) ? "determinate" : "indeterminate"}
          value={value}
          color={color}
        />
        {Boolean(EndAdornment) && (
          <div className="RydeProgress-EndAdornment">{EndAdornment}</div>
        )}
      </RydeProgressStyled>
    );
  },
  arePropsEqual,
);
